import {useCallback, useMemo, useState} from "react";
import filterResponse from "../filterResponse";

// Upload-time QA: the metrics a job's template marks "Run on upload", run against one file as soon
// as it has uploaded, so the contributor hears about a bad file while they can still swap it rather
// than after the entry is submitted. Submit still runs every metric, so this is an early warning,
// not the gate of record.
//
// Answers {checked, errors, qaBlocked}. `checked: false` means nothing ran — the input has no
// template, the template marks nothing to run on upload, or the workers could not be reached — and
// the file is let through without a verdict.
export async function checkUploadedFile({crowdJobId, fileInputId, key, fileName, answers, signal}) {
	const response = await fetch(process.env.REACT_APP_ENDPOINT + "/files/check", {
		method: "POST",
		credentials: "include",
		headers: {"Content-Type": "application/json"},
		body: JSON.stringify({crowdJobId, fileInputId, key, fileName, answers}),
		signal,
	});
	const closed = await filterResponse(response);

	if (closed?.jobClosed || response.status !== 200) {
		if (!closed?.jobClosed) {
			console.warn(`Upload QA check for ${key} answered ${response.status}; letting the file through unchecked.`);
		}
		return {checked: false, errors: [], qaBlocked: false};
	}

	return response.json();
}

// Which file inputs on a form are holding Submit — one still being checked, or one whose file a
// blocker metric failed — and the warnings the others left. Kept per input because every input on
// the form shares it: with a single flag, the first input to clear would release Submit while
// another still had a blocked file.
//
// Answers [held, setUploadQaHold, warnings]. A warning does not hold Submit; the contributor is
// asked to confirm instead (useUploadQaWarningsConfirm).
export function useUploadQaHolds() {
	const [inputs, setInputs] = useState({});

	const setUploadQaHold = useCallback((fileInputId, holding, warnings = []) => {
		setInputs(prev => {
			const current = prev[fileInputId];
			if (!holding && warnings.length === 0) {
				if (!current) {
					return prev;
				}
				const next = {...prev};
				delete next[fileInputId];
				return next;
			}
			if (current && current.holding === holding && current.warnings.join("\n") === warnings.join("\n")) {
				return prev;
			}
			return {...prev, [fileInputId]: {holding, warnings}};
		});
	}, []);

	const held = useMemo(() => Object.values(inputs).some(input => input.holding), [inputs]);
	const warnings = useMemo(() => Object.values(inputs).flatMap(input => input.warnings), [inputs]);

	return [held, setUploadQaHold, warnings];
}

